import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

const TOOLBAR = [
	{ command: 'bold', label: 'B', title: 'Bold', className: 'font-bold' },
	{ command: 'italic', label: 'I', title: 'Italic', className: 'italic' },
	{ command: 'underline', label: 'U', title: 'Underline', className: 'underline' },
	{ command: 'insertUnorderedList', label: '•', title: 'Bulleted list', className: 'text-lg leading-none' },
	{ command: 'createLink', label: 'Link', title: 'Add link', className: 'text-xs' },
]

// A small rich-text editor on the browser's own editing, enough for template
// bodies; the server cleans the HTML it produces.
// Only web and email links; a bare domain like example.com becomes https://.
const toLinkUrl = input => {
	const url = (input || '').trim()
	if (/^(https?:\/\/|mailto:)/i.test(url)) return url
	if (/^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(url)) return `https://${url}`
	return null
}

const TemplateBodyEditor = forwardRef(({ initialHtml, onChange, onFocus, isLight }, ref) => {
	const editorRef = useRef(null)
	// Where the user's caret last was, so a placeholder lands there and not at
	// the start, where focusing the editor alone would put it.
	const savedRange = useRef(null)

	useEffect(() => {
		editorRef.current.innerHTML = initialHtml || ''
		// Only the first value: after that the editor owns its content.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const emitChange = () => onChange(editorRef.current.innerHTML)

	const saveCaret = () => {
		const selection = window.getSelection()
		if (selection.rangeCount && editorRef.current.contains(selection.getRangeAt(0).commonAncestorContainer)) {
			savedRange.current = selection.getRangeAt(0).cloneRange()
		}
	}

	const restoreCaretOrEnd = () => {
		const range = savedRange.current || document.createRange()
		if (!savedRange.current) {
			range.selectNodeContents(editorRef.current)
			range.collapse(false)
		}
		const selection = window.getSelection()
		selection.removeAllRanges()
		selection.addRange(range)
	}

	useImperativeHandle(ref, () => ({
		setHtml: html => {
			editorRef.current.innerHTML = html
			savedRange.current = null
			emitChange()
		},
		insertText: text => {
			editorRef.current.focus()
			restoreCaretOrEnd()
			document.execCommand('insertText', false, text)
			saveCaret()
			emitChange()
		},
	}))

	const runCommand = command => {
		editorRef.current.focus()
		if (command === 'createLink') {
			const url = toLinkUrl(window.prompt('Link address (https://… or mailto:…)'))
			if (!url) return
			document.execCommand('createLink', false, url)
		} else {
			document.execCommand(command)
		}
		emitChange()
	}

	return (
		<div className={`rounded-lg overflow-hidden ${isLight ? 'ring-1 ring-gray-300 bg-white' : 'ring-1 ring-white/10 bg-[#1a2634]/60'}`}>
			<div className={`flex items-center gap-1 px-2 py-1.5 border-b ${isLight ? 'border-gray-200 text-gray-700' : 'border-white/10 text-gray-300'}`}>
				{TOOLBAR.map(tool => (
					<button
						key={tool.command}
						type="button"
						title={tool.title}
						aria-label={tool.title}
						onMouseDown={event => event.preventDefault()}
						onClick={() => runCommand(tool.command)}
						className={`min-w-[2rem] h-8 px-2 rounded ${tool.className} ${isLight ? 'hover:bg-gray-100' : 'hover:bg-white/10'}`}
					>
						{tool.label}
					</button>
				))}
			</div>
			<div
				ref={editorRef}
				contentEditable
				role="textbox"
				aria-multiline="true"
				aria-label="Template body"
				onInput={() => {
					saveCaret()
					emitChange()
				}}
				onKeyUp={saveCaret}
				onMouseUp={saveCaret}
				onFocus={onFocus}
				className={`min-h-[10rem] px-3 py-2.5 text-sm leading-relaxed focus:outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_a]:text-blue-500 [&_a]:underline ${
					isLight ? 'text-gray-900' : 'text-white'
				}`}
			/>
		</div>
	)
})

export default TemplateBodyEditor
